import { yupResolver } from '@hookform/resolvers/yup';
import { useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import AlertModal from 'components/common/AlertModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import ImageUploadControl from 'components/formfields/ImageUploadControl';
import { DINE_IN, SALES_CHANNEL_SOURCE } from 'features/MenuBook/constants';
import menuBookService from 'features/MenuBook/menuBookService';
import { MenuBook } from 'features/MenuBook/models/menuBook';
import { selectIsEditMode } from 'features/common/editModeSlice';
import masterDataService from 'features/common/masterDataService';
import { MenuBookProperty } from 'features/common/models';
import { useEffect, useRef, useState } from 'react';
import { Button, Form, Modal, Table } from 'react-bootstrap';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IMAGE_SIZES, displayError } from 'utils';
import { handleServiceError } from 'utils/axiosError';
import * as yup from 'yup';
import './style.scss';

const MENU_BOOK_PROPERTY_DEFAULT = 1;
const DEFAULT_BACKGROUND_COLOR = '#ffffff';
const DEFAULT_TEXT_COLOR = '#000000';

export interface MenuBookDetailModalProps {
  menuBookId?: number;
  copyFromMenuBookId?: number;
  onClose?: (isDataChanged: boolean) => void;
}

export default function MenuBookDetailModal({ menuBookId, copyFromMenuBookId, onClose }: MenuBookDetailModalProps) {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();
  const [isLoading, setIsLoading] = useState(true);
  const [menuBookPropertySource, setMenuBookPropertySource] = useState<{ [key: number]: string }>();
  const [menuBookData, setMenuBookData] = useState<MenuBook>();

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  // ref
  const backgroundSettingInputRef = useRef<HTMLInputElement>(null);
  const colorSettingInputRef = useRef<HTMLInputElement>(null);

  // yup setup
  const schema = yup.object({
    name: yup.string().required(t('MenuBookDetailModal.warn_field_required')),
    priority: yup
      .number()
      .min(0, t('MenuBookDetailModal.warn_value_number_greater_equal_0'))
      .integer(t('MenuBookDetailModal.warn_value_number_integer'))
      .transform((v, o) => (typeof o === 'string' && o.trim() === '' ? undefined : v))
      .typeError(t('MenuBookDetailModal.warn_field_required'))
      .required(t('MenuBookDetailModal.warn_field_required')),
  });

  // form setup
  const {
    register,
    handleSubmit,
    control,
    reset,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<Partial<MenuBook>>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      property: MENU_BOOK_PROPERTY_DEFAULT,
      sales_channel: DINE_IN,
      priority: 1,

      display_name_ja: '',
      display_name_en: '',
      display_name_vi: '',
      display_name_ko: '',
      display_name_zh_hant: '',
      display_name_zh_hans: '',

      mobile_order_img_ja: '',
      mobile_order_img_en: '',
      mobile_order_img_vi: '',
      mobile_order_img_ko: '',
      mobile_order_img_zh_hant: '',
      mobile_order_img_zh_hans: '',

      tablet_order_img_ja: '',
      tablet_order_img_en: '',
      tablet_order_img_vi: '',
      tablet_order_img_ko: '',
      tablet_order_img_zh_hant: '',
      tablet_order_img_zh_hans: '',

      note: '',
      background_color: DEFAULT_BACKGROUND_COLOR,
      text_color: DEFAULT_TEXT_COLOR,
      background_flg: true,

      background_img_ja: '',
      background_img_en: '',
      background_img_vi: '',
      background_img_ko: '',
      background_img_zh_hant: '',
      background_img_zh_hans: '',
    },
    mode: 'onSubmit',
  });

  const watchName = useWatch({ control, name: 'name', defaultValue: '' });
  const watchBackgroundColor = useWatch({ control, name: 'background_color', defaultValue: DEFAULT_BACKGROUND_COLOR });
  const watchTextColor = useWatch({ control, name: 'text_color', defaultValue: DEFAULT_TEXT_COLOR });
  const watchBackgroundFlg = useWatch({ control, name: 'background_flg', defaultValue: true });

  useEffect(() => {
    //load data for property source
    (async () => {
      try {
        const response = await masterDataService.getMenuBookProperty();
        const menuBookPropertyMap = response.data.menu_book_property.reduce(
          (menuBookPropertyMap: { [key: number]: string }, menuBookProperty: MenuBookProperty) => {
            menuBookPropertyMap[menuBookProperty.id] = menuBookProperty.name;
            return menuBookPropertyMap;
          },
          {}
        );
        setMenuBookPropertySource(menuBookPropertyMap);
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();

    //load data menuBook from menuBookId (edit form)
    if (menuBookId) {
      (async () => {
        const response = await menuBookService.getItem(menuBookId);
        setMenuBookData(response.data);
        reset(response.data);
      })();
    }

    //load data menuBook from copyFromMenuBookId
    if (copyFromMenuBookId) {
      (async () => {
        const response = await menuBookService.getItem(copyFromMenuBookId);
        setMenuBookData(response.data);
        reset(response.data);
        setValue('copyFromMenuBookId', copyFromMenuBookId);
      })();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (menuBookId) {
      //check loading form edit
      if (menuBookPropertySource && menuBookData) {
        setIsLoading(false);
      }
    } else {
      // check loading form add
      if (menuBookPropertySource) {
        setIsLoading(false);
      }
    }
  }, [menuBookPropertySource, menuBookData, menuBookId]);

  function handleBackgroundSettingClick() {
    if (backgroundSettingInputRef.current) {
      backgroundSettingInputRef.current.click();
    }
  }

  function handleColorSettingClick() {
    if (colorSettingInputRef.current) {
      colorSettingInputRef.current.click();
    }
  }

  function onSubmit(data: Partial<MenuBook>) {
    if (menuBookId) {
      //update data
      (async () => {
        try {
          const response = await menuBookService.update(menuBookId, data);
          showGlobalModal(() => (
            <AlertModal title={t('App.info')} message={response.message} onClose={handleClose.bind(null, true)} />
          ));
        } catch (e) {
          if (!handleServiceError(showGlobalModal, e as AxiosError)) {
            displayError(showGlobalModal, e as AxiosError);
          }
        }
      })();
    } else {
      // insert data
      (async () => {
        try {
          const response = await menuBookService.add(data);
          showGlobalModal(() => (
            <AlertModal title={t('App.info')} message={response.message} onClose={handleClose.bind(null, true)} />
          ));
        } catch (e) {
          if (!handleServiceError(showGlobalModal, e as AxiosError)) {
            displayError(showGlobalModal, e as AxiosError);
          }
        }
      })();
    }
  }

  function handleClose(isDataChanged: boolean = false) {
    onClose && onClose(isDataChanged);
  }

  if (isLoading) {
    return null;
  }

  return (
    <Modal dialogClassName="menubook-detail-modal" show backdrop="static" onHide={handleClose} centered>
      <Modal.Header closeButton className={isEditMode ? 'reedit-mode-wrapper' : ''}>
        <FeatureTitleBar title={t('MenuBookDetailModal.head_title')} />
      </Modal.Header>
      <Modal.Body className={isEditMode ? 'reedit-mode-wrapper' : ''}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="d-flex justify-content-between my-3">
            <div className="pe-3">
              <div>{t('MenuBookDetailModal.guide_title')}</div>
              <div className="ps-3">{t('MenuBookDetailModal.guide_description')}</div>
            </div>
            <div className="ps-3">
              <Button type="submit" className="btn btn--dark-green">
                {t('MenuBookDetailModal.save')}
              </Button>
            </div>
          </div>

          <Table bordered className="form-menu-book">
            <tbody>
              <tr>
                <th colSpan={3}>{t('MenuBookDetailModal.menu_book_name')}</th>
                <td colSpan={3}>
                  <Form.Control className="text-center" {...register('name')}></Form.Control>
                  {errors.name && <small className="text-danger d-inline-block mt-1">{errors.name.message}</small>}
                </td>
              </tr>
              <tr>
                <th colSpan={3}>{t('MenuBookDetailModal.sales_channel')}</th>
                <td colSpan={3}>
                  {SALES_CHANNEL_SOURCE && (
                    <Form.Select className="text-center" {...register('sales_channel')}>
                      {Object.keys(SALES_CHANNEL_SOURCE).map(function (key) {
                        return (
                          <option key={key} value={key}>
                            {SALES_CHANNEL_SOURCE[Number(key)]}
                          </option>
                        );
                      })}
                    </Form.Select>
                  )}
                </td>
              </tr>
              <tr>
                <th colSpan={3}>{t('MenuBookDetailModal.property')}</th>
                <td colSpan={3}>
                  {menuBookPropertySource && (
                    <Form.Select className="text-center" {...register('property')}>
                      {Object.keys(menuBookPropertySource).map(function (key) {
                        return (
                          <option key={key} value={key}>
                            {menuBookPropertySource[Number(key)]}
                          </option>
                        );
                      })}
                    </Form.Select>
                  )}
                </td>
              </tr>
              <tr>
                <th colSpan={3}>{t('MenuBookDetailModal.priority_order')}</th>
                <td colSpan={3}>
                  <span>{t('MenuBookDetailModal.priority_guide')}</span>
                  <Form.Control className="text-center" {...register('priority')} />
                  {errors.priority && (
                    <small className="text-danger d-inline-block mt-1">{errors.priority.message}</small>
                  )}
                </td>
              </tr>
              <tr>
                <th rowSpan={watchBackgroundFlg ? 9 : 3} style={{ width: '10%' }}>
                  {t('MenuBookDetailModal.button_heading_settings')}
                </th>
                <th colSpan={2} rowSpan={2}>
                  {t('MenuBookDetailModal.color_setting')}
                </th>
                <td rowSpan={2}>
                  <div className="text-center mb-2">{t('MenuBookDetailModal.color_setting_guide')}</div>
                  <div
                    style={{ background: watchBackgroundColor, color: watchTextColor, fontSize: '1rem' }}
                    className="text-center p-1"
                  >
                    {watchName}
                  </div>
                </td>
                <th style={{ width: '10%' }}>{t('MenuBookDetailModal.background')}</th>
                <td width="10%">
                  <Button
                    className="d-inline-block"
                    style={{ minWidth: '2rem' }}
                    onClick={handleBackgroundSettingClick}
                  >
                    {t('App.change')}
                  </Button>
                  <Form.Control
                    ref={backgroundSettingInputRef}
                    type="color"
                    id="background-setting"
                    className="invisible"
                    style={{ height: 0, padding: 0 }}
                    defaultValue="#563d7c"
                    onChange={(event) => {
                      setValue('background_color', event.target.value);
                    }}
                  />
                </td>
              </tr>
              <tr>
                <th>{t('MenuBookDetailModal.color')}</th>
                <td>
                  <Button className="d-inline-block" style={{ minWidth: '2rem' }} onClick={handleColorSettingClick}>
                    {t('App.change')}
                  </Button>
                  <Form.Control
                    ref={colorSettingInputRef}
                    type="color"
                    id="color-setting"
                    className="invisible"
                    style={{ height: 0, padding: 0 }}
                    defaultValue="#563d7c"
                    title="Choose your color"
                    onChange={(event) => {
                      setValue('text_color', event.target.value);
                    }}
                  />
                </td>
              </tr>
              <tr>
                <th rowSpan={watchBackgroundFlg ? 7 : 1} style={{ width: '4%' }}>
                  {t('MenuBookDetailModal.for_tablet_order')}
                </th>
                <th style={{ width: '16%' }}>{t('MenuBookDetailModal.background_setting')}</th>
                <td colSpan={3}>
                  <div>{t('MenuBookDetailModal.background_setting_guide')}</div>
                  <div className="d-flex justify-content-center">
                    <Form.Check
                      type="switch"
                      label=""
                      size={1}
                      defaultChecked={getValues('background_flg')}
                      onChange={(event) => {
                        setValue('background_flg', event.target.checked);
                      }}
                    />
                  </div>
                </td>
              </tr>
              <tr className={!watchBackgroundFlg ? 'd-none' : ''}>
                <th>{t('MenuBookDetailModal.japanese')}</th>
                <td colSpan={3}>
                  <ImageUploadControl
                    name="background_img_ja"
                    control={control}
                    minWidth={IMAGE_SIZES.menu_book_tablet_background_img.minWidth}
                    minHeight={IMAGE_SIZES.menu_book_tablet_background_img.minHeight}
                    maxWidth={IMAGE_SIZES.menu_book_tablet_background_img.maxWidth}
                    maxHeight={IMAGE_SIZES.menu_book_tablet_background_img.maxHeight}
                    maxFileSize={IMAGE_SIZES.menu_book_tablet_background_img.maxFileSize}
                  />
                </td>
              </tr>
              <tr className={!watchBackgroundFlg ? 'd-none' : ''}>
                <th>{t('MenuBookDetailModal.english')}</th>
                <td colSpan={3}>
                  <ImageUploadControl
                    name="background_img_en"
                    control={control}
                    minWidth={IMAGE_SIZES.menu_book_tablet_background_img.minWidth}
                    minHeight={IMAGE_SIZES.menu_book_tablet_background_img.minHeight}
                    maxWidth={IMAGE_SIZES.menu_book_tablet_background_img.maxWidth}
                    maxHeight={IMAGE_SIZES.menu_book_tablet_background_img.maxHeight}
                    maxFileSize={IMAGE_SIZES.menu_book_tablet_background_img.maxFileSize}
                  />
                </td>
              </tr>
              <tr className={!watchBackgroundFlg ? 'd-none' : ''}>
                <th>{t('MenuBookDetailModal.simplified_chinese')}</th>
                <td colSpan={3}>
                  <ImageUploadControl
                    name="background_img_zh_hans"
                    control={control}
                    minWidth={IMAGE_SIZES.menu_book_tablet_background_img.minWidth}
                    minHeight={IMAGE_SIZES.menu_book_tablet_background_img.minHeight}
                    maxWidth={IMAGE_SIZES.menu_book_tablet_background_img.maxWidth}
                    maxHeight={IMAGE_SIZES.menu_book_tablet_background_img.maxHeight}
                    maxFileSize={IMAGE_SIZES.menu_book_tablet_background_img.maxFileSize}
                  />
                </td>
              </tr>
              <tr className={!watchBackgroundFlg ? 'd-none' : ''}>
                <th>{t('MenuBookDetailModal.transitional_chinese')}</th>
                <td colSpan={3}>
                  <ImageUploadControl
                    name="background_img_zh_hant"
                    control={control}
                    minWidth={IMAGE_SIZES.menu_book_tablet_background_img.minWidth}
                    minHeight={IMAGE_SIZES.menu_book_tablet_background_img.minHeight}
                    maxWidth={IMAGE_SIZES.menu_book_tablet_background_img.maxWidth}
                    maxHeight={IMAGE_SIZES.menu_book_tablet_background_img.maxHeight}
                    maxFileSize={IMAGE_SIZES.menu_book_tablet_background_img.maxFileSize}
                  />
                </td>
              </tr>
              <tr className={!watchBackgroundFlg ? 'd-none' : ''}>
                <th>{t('MenuBookDetailModal.korean')}</th>
                <td colSpan={3}>
                  <ImageUploadControl
                    name="background_img_ko"
                    control={control}
                    minWidth={IMAGE_SIZES.menu_book_tablet_background_img.minWidth}
                    minHeight={IMAGE_SIZES.menu_book_tablet_background_img.minHeight}
                    maxWidth={IMAGE_SIZES.menu_book_tablet_background_img.maxWidth}
                    maxHeight={IMAGE_SIZES.menu_book_tablet_background_img.maxHeight}
                    maxFileSize={IMAGE_SIZES.menu_book_tablet_background_img.maxFileSize}
                  />
                </td>
              </tr>
              <tr className={!watchBackgroundFlg ? 'd-none' : ''}>
                <th>{t('MenuBookDetailModal.vietnamese')}</th>
                <td colSpan={3}>
                  <ImageUploadControl
                    name="background_img_vi"
                    control={control}
                    minWidth={IMAGE_SIZES.menu_book_tablet_background_img.minWidth}
                    minHeight={IMAGE_SIZES.menu_book_tablet_background_img.minHeight}
                    maxWidth={IMAGE_SIZES.menu_book_tablet_background_img.maxWidth}
                    maxHeight={IMAGE_SIZES.menu_book_tablet_background_img.maxHeight}
                    maxFileSize={IMAGE_SIZES.menu_book_tablet_background_img.maxFileSize}
                  />
                </td>
              </tr>
              <tr>
                <th rowSpan={6} colSpan={2} style={{ width: '25%' }}>
                  {t('MenuBookDetailModal.display_name')}
                </th>
                <th style={{ width: '25%' }}>{t('MenuBookDetailModal.japanese')}</th>
                <td colSpan={3} style={{ width: '50%' }}>
                  <Form.Control {...register('display_name_ja')}></Form.Control>
                </td>
              </tr>
              <tr>
                <th>{t('MenuBookDetailModal.english')}</th>
                <td colSpan={3}>
                  <Form.Control {...register('display_name_en')}></Form.Control>
                </td>
              </tr>
              <tr>
                <th>{t('MenuBookDetailModal.simplified_chinese')}</th>
                <td colSpan={3}>
                  <Form.Control {...register('display_name_zh_hans')}></Form.Control>
                </td>
              </tr>
              <tr>
                <th>{t('MenuBookDetailModal.transitional_chinese')}</th>
                <td colSpan={3}>
                  <Form.Control {...register('display_name_zh_hant')}></Form.Control>
                </td>
              </tr>
              <tr>
                <th>{t('MenuBookDetailModal.korean')}</th>
                <td colSpan={3}>
                  <Form.Control {...register('display_name_ko')}></Form.Control>
                </td>
              </tr>
              <tr>
                <th>{t('MenuBookDetailModal.vietnamese')}</th>
                <td colSpan={3}>
                  <Form.Control {...register('display_name_vi')}></Form.Control>
                </td>
              </tr>
              {/* group display_name for mobile */}
              <tr>
                <th rowSpan={12}>
                  {t('MenuBookDetailModal.introduce_image')}
                  <br />
                  {t('MenuBookDetailModal.introduce_image_note')}
                </th>
                <th rowSpan={6}>{t('MenuBookDetailModal.for_mobile_order')}</th>
                <th>{t('MenuBookDetailModal.japanese')}</th>
                <td colSpan={3}>
                  <ImageUploadControl
                    name="mobile_order_img_ja"
                    control={control}
                    minWidth={IMAGE_SIZES.menu_book_mobile_introduce_img.minWidth}
                    minHeight={IMAGE_SIZES.menu_book_mobile_introduce_img.minHeight}
                    maxWidth={IMAGE_SIZES.menu_book_mobile_introduce_img.maxWidth}
                    maxHeight={IMAGE_SIZES.menu_book_mobile_introduce_img.maxHeight}
                    maxFileSize={IMAGE_SIZES.menu_book_mobile_introduce_img.maxFileSize}
                  />
                </td>
              </tr>
              <tr>
                <th>{t('MenuBookDetailModal.english')}</th>
                <td colSpan={3}>
                  <ImageUploadControl
                    name="mobile_order_img_en"
                    control={control}
                    minWidth={IMAGE_SIZES.menu_book_mobile_introduce_img.minWidth}
                    minHeight={IMAGE_SIZES.menu_book_mobile_introduce_img.minHeight}
                    maxWidth={IMAGE_SIZES.menu_book_mobile_introduce_img.maxWidth}
                    maxHeight={IMAGE_SIZES.menu_book_mobile_introduce_img.maxHeight}
                    maxFileSize={IMAGE_SIZES.menu_book_mobile_introduce_img.maxFileSize}
                  />
                </td>
              </tr>
              <tr>
                <th>{t('MenuBookDetailModal.simplified_chinese')}</th>
                <td colSpan={3}>
                  <ImageUploadControl
                    name="mobile_order_img_zh_hans"
                    control={control}
                    minWidth={IMAGE_SIZES.menu_book_mobile_introduce_img.minWidth}
                    minHeight={IMAGE_SIZES.menu_book_mobile_introduce_img.minHeight}
                    maxWidth={IMAGE_SIZES.menu_book_mobile_introduce_img.maxWidth}
                    maxHeight={IMAGE_SIZES.menu_book_mobile_introduce_img.maxHeight}
                    maxFileSize={IMAGE_SIZES.menu_book_mobile_introduce_img.maxFileSize}
                  />
                </td>
              </tr>
              <tr>
                <th>{t('MenuBookDetailModal.transitional_chinese')}</th>
                <td colSpan={3}>
                  <ImageUploadControl
                    name="mobile_order_img_zh_hant"
                    control={control}
                    minWidth={IMAGE_SIZES.menu_book_mobile_introduce_img.minWidth}
                    minHeight={IMAGE_SIZES.menu_book_mobile_introduce_img.minHeight}
                    maxWidth={IMAGE_SIZES.menu_book_mobile_introduce_img.maxWidth}
                    maxHeight={IMAGE_SIZES.menu_book_mobile_introduce_img.maxHeight}
                    maxFileSize={IMAGE_SIZES.menu_book_mobile_introduce_img.maxFileSize}
                  />
                </td>
              </tr>
              <tr>
                <th>{t('MenuBookDetailModal.korean')}</th>
                <td colSpan={3}>
                  <ImageUploadControl
                    name="mobile_order_img_ko"
                    control={control}
                    minWidth={IMAGE_SIZES.menu_book_mobile_introduce_img.minWidth}
                    minHeight={IMAGE_SIZES.menu_book_mobile_introduce_img.minHeight}
                    maxWidth={IMAGE_SIZES.menu_book_mobile_introduce_img.maxWidth}
                    maxHeight={IMAGE_SIZES.menu_book_mobile_introduce_img.maxHeight}
                    maxFileSize={IMAGE_SIZES.menu_book_mobile_introduce_img.maxFileSize}
                  />
                </td>
              </tr>
              <tr>
                <th>{t('MenuBookDetailModal.vietnamese')}</th>
                <td colSpan={3}>
                  <ImageUploadControl
                    name="mobile_order_img_vi"
                    control={control}
                    minWidth={IMAGE_SIZES.menu_book_mobile_introduce_img.minWidth}
                    minHeight={IMAGE_SIZES.menu_book_mobile_introduce_img.minHeight}
                    maxWidth={IMAGE_SIZES.menu_book_mobile_introduce_img.maxWidth}
                    maxHeight={IMAGE_SIZES.menu_book_mobile_introduce_img.maxHeight}
                    maxFileSize={IMAGE_SIZES.menu_book_mobile_introduce_img.maxFileSize}
                  />
                </td>
              </tr>
              <tr>
                <th rowSpan={6}>{t('MenuBookDetailModal.for_tablet_order')}</th>
                <th>{t('MenuBookDetailModal.japanese')}</th>
                <td colSpan={3}>
                  <ImageUploadControl
                    name="tablet_order_img_ja"
                    control={control}
                    minWidth={IMAGE_SIZES.menu_book_tablet_introduce_img.minWidth}
                    minHeight={IMAGE_SIZES.menu_book_tablet_introduce_img.minHeight}
                    maxWidth={IMAGE_SIZES.menu_book_tablet_introduce_img.maxWidth}
                    maxHeight={IMAGE_SIZES.menu_book_tablet_introduce_img.maxHeight}
                    maxFileSize={IMAGE_SIZES.menu_book_tablet_introduce_img.maxFileSize}
                  />
                </td>
              </tr>
              <tr>
                <th>{t('MenuBookDetailModal.english')}</th>
                <td colSpan={3}>
                  <ImageUploadControl
                    name="tablet_order_img_en"
                    control={control}
                    minWidth={IMAGE_SIZES.menu_book_tablet_introduce_img.minWidth}
                    minHeight={IMAGE_SIZES.menu_book_tablet_introduce_img.minHeight}
                    maxWidth={IMAGE_SIZES.menu_book_tablet_introduce_img.maxWidth}
                    maxHeight={IMAGE_SIZES.menu_book_tablet_introduce_img.maxHeight}
                    maxFileSize={IMAGE_SIZES.menu_book_tablet_introduce_img.maxFileSize}
                  />
                </td>
              </tr>
              <tr>
                <th>{t('MenuBookDetailModal.simplified_chinese')}</th>
                <td colSpan={3}>
                  <ImageUploadControl
                    name="tablet_order_img_zh_hans"
                    control={control}
                    minWidth={IMAGE_SIZES.menu_book_tablet_introduce_img.minWidth}
                    minHeight={IMAGE_SIZES.menu_book_tablet_introduce_img.minHeight}
                    maxWidth={IMAGE_SIZES.menu_book_tablet_introduce_img.maxWidth}
                    maxHeight={IMAGE_SIZES.menu_book_tablet_introduce_img.maxHeight}
                    maxFileSize={IMAGE_SIZES.menu_book_tablet_introduce_img.maxFileSize}
                  />
                </td>
              </tr>
              <tr>
                <th>{t('MenuBookDetailModal.transitional_chinese')}</th>
                <td colSpan={3}>
                  <ImageUploadControl
                    name="tablet_order_img_zh_hant"
                    control={control}
                    minWidth={IMAGE_SIZES.menu_book_tablet_introduce_img.minWidth}
                    minHeight={IMAGE_SIZES.menu_book_tablet_introduce_img.minHeight}
                    maxWidth={IMAGE_SIZES.menu_book_tablet_introduce_img.maxWidth}
                    maxHeight={IMAGE_SIZES.menu_book_tablet_introduce_img.maxHeight}
                    maxFileSize={IMAGE_SIZES.menu_book_tablet_introduce_img.maxFileSize}
                  />
                </td>
              </tr>
              <tr>
                <th>{t('MenuBookDetailModal.korean')}</th>
                <td colSpan={3}>
                  <ImageUploadControl
                    name="tablet_order_img_ko"
                    control={control}
                    minWidth={IMAGE_SIZES.menu_book_tablet_introduce_img.minWidth}
                    minHeight={IMAGE_SIZES.menu_book_tablet_introduce_img.minHeight}
                    maxWidth={IMAGE_SIZES.menu_book_tablet_introduce_img.maxWidth}
                    maxHeight={IMAGE_SIZES.menu_book_tablet_introduce_img.maxHeight}
                    maxFileSize={IMAGE_SIZES.menu_book_tablet_introduce_img.maxFileSize}
                  />
                </td>
              </tr>
              <tr>
                <th>{t('MenuBookDetailModal.vietnamese')}</th>
                <td colSpan={3}>
                  <ImageUploadControl
                    name="tablet_order_img_vi"
                    control={control}
                    minWidth={IMAGE_SIZES.menu_book_tablet_introduce_img.minWidth}
                    minHeight={IMAGE_SIZES.menu_book_tablet_introduce_img.minHeight}
                    maxWidth={IMAGE_SIZES.menu_book_tablet_introduce_img.maxWidth}
                    maxHeight={IMAGE_SIZES.menu_book_tablet_introduce_img.maxHeight}
                    maxFileSize={IMAGE_SIZES.menu_book_tablet_introduce_img.maxFileSize}
                  />
                </td>
              </tr>
              <tr>
                <th colSpan={3}>{t('MenuBookDetailModal.remark')}</th>
                <td colSpan={3}>
                  <Form.Control as="textarea" rows={3} {...register('note')}></Form.Control>
                </td>
              </tr>
            </tbody>
          </Table>
        </form>
      </Modal.Body>
    </Modal>
  );
}
